<template>
  <!-- Right-icon mode: custom wrapper with clickable search icon on the right -->
  <div v-if="rightIcon" class="search-bar-right-icon" :class="customClass">
    <VTextField
      v-model="searchTerm"
      :placeholder="placeholder"
      type="search"
      appearance="ghost"
      class="vivid-input search-bar-input"
      :style="{ background: backgroundColor }"
      :aria-label="placeholder"
      @keyup.enter="triggerSearch"
      @input="onSearchInput"
      @blur="$emit('blur', $event)"
    ></VTextField>
    <VIcon
      name="search-line"
      class="search-bar-icon"
      role="button"
      :aria-label="placeholder"
      tabindex="0"
      @click="triggerSearch"
      @keyup.enter="triggerSearch"
    ></VIcon>
  </div>

  <!-- Default mode: standard Vivid VTextField with built-in left icon -->
  <div v-else class="search-bar-container">
    <VTextField
      v-model="searchTerm"
      :placeholder="placeholder"
      :class="['custom-searchbar', customClass]"
      appearance="ghost"
      class="vivid-input"
      icon="search-line"
      @input="onSearchInput"
    ></VTextField>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Search...'
  },
  customClass: {
    type: String,
    default: ''
  },
  /**
   * When true: renders the right-side clickable search icon pattern
   * (used on Broadcast, Segments, Message Templates pages).
   * When false (default): renders the standard Vivid VTextField with
   * the built-in left icon — search fires on every keystroke.
   */
  rightIcon: {
    type: Boolean,
    default: false
  },
  /**
   * Background color of the VTextField in right-icon mode.
   * Accepts any CSS color value or CSS variable.
   * Defaults to white. Pass 'var(--background-primary)' for the gray variant.
   */
  backgroundColor: {
    type: String,
    default: 'var(--white)'
  },
  // Legacy props kept for backward compatibility
  clearIcon: {
    type: [String, Boolean],
    default: false
  },
  showNoResults: {
    type: Boolean,
    default: false
  },
  noResultsMessage: {
    type: String,
    default: 'No results found'
  },
  loading: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'search-input', 'search', 'blur']);

const searchTerm = ref(props.modelValue);

watch(() => props.modelValue, (newValue) => {
  searchTerm.value = newValue;
});

watch(searchTerm, (newValue) => {
  emit('update:modelValue', newValue);
});

const onSearchInput = (event) => {
  emit('search-input', { value: searchTerm.value, event });
};

const triggerSearch = () => {
  if (!searchTerm.value || !searchTerm.value.trim()) return;
  emit('search', searchTerm.value);
};

const clearSearch = () => {
  searchTerm.value = '';
  emit('update:modelValue', '');
  emit('search-input', { value: '', event: null });
};

defineExpose({ clearSearch, focus: () => {} });
</script>

<style scoped>
/* Default mode */
.search-bar-container {
  width: 100%;
}

.custom-searchbar {
  width: 100%;
}

/* Right-icon mode */
.search-bar-right-icon {
  display: flex;
  align-items: center;
  position: relative;
  border-radius: 25px;
  padding: 4px 12px;
}

.search-bar-right-icon .search-bar-input {
  width: 100%;
  border-radius: 25px;
}

.search-bar-right-icon .search-bar-icon {
  position: absolute;
  top: 50%;
  right: 26px;
  transform: translateY(-50%);
  cursor: pointer;
  z-index: 1;
  color: var(--gray-04);
}
</style>
